/**
 * useOnline — VÉRAULT online/offline awareness (Wave 30 · Phase 7b)
 * ─────────────────────────────────────────────────────────────────
 * Single source of truth for connectivity. Combines:
 *   - navigator.onLine (cheap baseline; can be wrong but useful)
 *   - window 'online' / 'offline' events
 *   - Background HEAD ping every 30s to /api/health (or root)
 *     so we catch silent failures (captive portals, dead Wi-Fi)
 *
 * Surfaces:
 *   - isOnline (boolean) — best-effort current state
 *   - lastCheckedAt (Date) — last successful or attempted ping
 *
 * Used by:
 *   - OfflineBanner in PortalShell
 *   - OnlineProvider to broadcast to action shelves
 */
import { useCallback, useEffect, useRef, useState } from "react";

const PING_INTERVAL_MS = 30000;
const PING_TIMEOUT_MS = 6000;
const PING_URL = "/api/health";

async function pingOnce() {
  try {
    const controller = new AbortController();
    const t = window.setTimeout(() => controller.abort(), PING_TIMEOUT_MS);
    const res = await fetch(PING_URL, {
      method: "GET",
      cache: "no-store",
      signal: controller.signal,
      credentials: "omit",
    });
    window.clearTimeout(t);
    // Any response that isn't network-failure counts as "online".
    // Even a 401 or 404 means the network round-tripped.
    return res && (res.status >= 200 && res.status < 600);
  } catch {
    return false;
  }
}

export function useOnline() {
  const [isOnline, setIsOnline] = useState(() => {
    if (typeof navigator === "undefined") return true;
    return navigator.onLine !== false;
  });
  const [lastCheckedAt, setLastCheckedAt] = useState(() => new Date());
  const pingTimer = useRef(null);

  const runPing = useCallback(async () => {
    const ok = await pingOnce();
    setLastCheckedAt(new Date());
    setIsOnline((prev) => (prev === ok ? prev : ok));
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const onOnline = () => {
      setIsOnline(true);
      // Confirm with a real ping so we don't bounce optimistic.
      runPing();
    };
    const onOffline = () => setIsOnline(false);

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);

    // Initial ping after a short delay (let the app settle).
    const initial = window.setTimeout(runPing, 4000);

    // Background ping cadence — paused on hidden tab.
    const scheduleNext = () => {
      pingTimer.current = window.setTimeout(async () => {
        if (document.visibilityState === "visible") {
          await runPing();
        }
        scheduleNext();
      }, PING_INTERVAL_MS);
    };
    scheduleNext();

    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.clearTimeout(initial);
      if (pingTimer.current) window.clearTimeout(pingTimer.current);
    };
  }, [runPing]);

  return { isOnline, lastCheckedAt, recheck: runPing };
}

export default useOnline;
